<script>
import ActionButton from '@modules/back-office/components/action-button/action-button.vue'

export default {
  name: 'ValidationErrors',
  components: { ActionButton },
  props: {
    errors: {
      type: Array,
      default: () => [],
    },
    icon: {
      type: String,
      default: 'warning',
    },
    iconColor: {
      type: String,
      default: 'warning',
    },
  },
  data() {
    return {
      currentIndex: 0,
      expanded: false,
    }
  },
  computed: {
    total() {
      return this.errors.length
    },
    hasErrors() {
      return this.total > 0
    },
    hasMultiple() {
      return this.total > 1
    },
    isMessageClickable() {
      return Boolean(this.$listeners.select)
    },
    currentMessage() {
      return this.errors[this.currentIndex] || ''
    },
    counterLabel() {
      return `${this.currentIndex + 1} de ${this.total} alertas`
    },
    isFirst() {
      return this.currentIndex <= 0
    },
    isLast() {
      return this.currentIndex >= this.total - 1
    },
  },
  watch: {
    total(value) {
      if (this.currentIndex > value - 1) {
        this.currentIndex = Math.max(0, value - 1)
      }
      if (value <= 1) {
        this.expanded = false
      }
    },
  },
  methods: {
    selectMessage(index) {
      if (this.isMessageClickable) {
        this.$emit('select', index)
      }
    },
    prev() {
      if (!this.isFirst) {
        this.currentIndex -= 1
        this.$emit('select', this.currentIndex)
      }
    },
    next() {
      if (!this.isLast) {
        this.currentIndex += 1
        this.$emit('select', this.currentIndex)
      }
    },
    expand() {
      this.expanded = true
    },
    collapseTo(index) {
      this.currentIndex = index
      this.expanded = false
      this.$emit('select', index)
    },
  },
}
</script>

<template>
  <div v-if="hasErrors" class="validation-errors">
    <div v-if="expanded" class="validation-errors__list">
      <BaseAlert
        v-for="(message, index) in errors"
        :key="`erro-${index}`"
        :icon="icon"
        :icon-color="iconColor"
        class="validation-errors__item"
      >
        <span
          class="validation-errors__message"
          :class="{ 'validation-errors__message--clickable': isMessageClickable }"
          @click="selectMessage(index)"
        >
          {{ message }}
        </span>
        <template v-slot:button>
          <div class="validation-errors__actions">
            <slot name="action" :index="index" />
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="validation-errors__collapse"
                  v-bind="attrs"
                  v-on="on"
                >
                  <ActionButton
                    icon-only
                    small
                    icon-color="dark"
                    icon-name="arrow-down-new-ux"
                    class="validation-errors__arrow validation-errors__arrow--collapse"
                    @click="collapseTo(index)"
                  />
                </span>
              </template>
              <span>Recolher os alertas</span>
            </v-tooltip>
          </div>
        </template>
      </BaseAlert>
    </div>

    <BaseAlert v-else :icon="icon" :icon-color="iconColor">
      <span
        class="validation-errors__message"
        :class="{ 'validation-errors__message--clickable': isMessageClickable }"
        @click="selectMessage(currentIndex)"
      >
        {{ currentMessage }}
      </span>
      <template v-if="hasMultiple || $scopedSlots.action" v-slot:button>
        <div class="validation-errors__actions">
          <slot name="action" :index="currentIndex" />
          <div v-if="hasMultiple" class="validation-errors__nav">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="validation-errors__count"
                  v-bind="attrs"
                  v-on="on"
                  @click="expand"
                >
                  {{ counterLabel }}
                </button>
              </template>
              <span>Ver todos os alertas</span>
            </v-tooltip>
            <span class="validation-errors__divider" />
            <ActionButton
              icon-only
              small
              icon-color="dark"
              icon-name="arrow-down-new-ux"
              label="Anterior"
              class="validation-errors__arrow validation-errors__arrow--prev"
              :disabled="isFirst"
              @click="prev"
            />
            <ActionButton
              icon-only
              small
              icon-color="dark"
              icon-name="arrow-down-new-ux"
              label="Próximo"
              class="validation-errors__arrow validation-errors__arrow--next"
              :disabled="isLast"
              @click="next"
            />
          </div>
        </div>
      </template>
    </BaseAlert>
  </div>
</template>

<style lang="sass" scoped>
.validation-errors
  &__message
    &--clickable
      cursor: pointer
  &__actions
    display: inline-flex
    align-items: center
  &__list
    display: flex
    flex-direction: column
    gap: 0.75rem
    max-height: 30vh
    overflow-y: auto
  &__collapse
    display: inline-flex
    opacity: 0
    transition: opacity 120ms ease
  &__item:hover &__collapse
    opacity: 1
  &__nav
    display: inline-flex
    align-items: center
    padding: 0.25rem 0.5rem
    border: 1px solid var(--gray-3)
    border-radius: 8px
  &__count
    padding: 0 0.5rem
    font-size: 0.75rem
    white-space: nowrap
    background: transparent
    border: none
    cursor: pointer
  &__divider
    width: 1px
    align-self: stretch
    margin: 0.375rem 0.625rem
    background: var(--gray-3)
  &__arrow
    display: inline-flex
    &--prev
      ::v-deep .base-action-button__icon
        transform: rotate(90deg)
    &--next
      ::v-deep .base-action-button__icon
        transform: rotate(-90deg)
    &--collapse
      ::v-deep .base-action-button__icon
        transform: rotate(180deg)
</style>
